import type { FormField } from '../../interfaces/form.interface';
import type { CalendarConfiguration, InputType } from '../../interfaces/input.interface';
import { InputType as ReactiveInputType } from '../../interfaces/input.interface';
import {
    ReactiveDropdownFieldFactory,
    type ReactiveDropdownFieldFactoryConfig,
    ReactiveFieldFactory,
    type ReactiveGenericFieldFactoryConfig,
    ReactiveTextareaFieldFactory,
    type ReactiveTextareaFieldFactoryConfig,
} from './reactive-field-factory';

const DEFAULT_CLIENT_FIELD_NAME = 'id_cliente';
const DEFAULT_CLIENT_LABEL = 'Cliente';
const DEFAULT_DATE_FIELD_NAME = 'fecha';
const DEFAULT_DATE_LABEL = 'Fecha';
const DEFAULT_OBSERVATIONS_FIELD_NAME = 'observaciones';
const DEFAULT_OBSERVATIONS_LABEL = 'Observaciones';
const DEFAULT_PRODUCT_FIELD_NAME = 'id_producto';
const DEFAULT_PRODUCT_LABEL = 'Producto';
const DEFAULT_QUANTITY_FIELD_NAME = 'cantidad';
const DEFAULT_QUANTITY_LABEL = 'Cantidad';
const DEFAULT_UNIT_FIELD_NAME = 'unidad';
const DEFAULT_UNIT_LABEL = 'Unidad';
const DEFAULT_DROPDOWN_PRIMARY_KEY = 'id';
const DEFAULT_DATE_FORMAT = 'dd/mm/yy';

export type ReactiveDomainDropdownFieldFactoryConfig<TModel> = Omit<ReactiveDropdownFieldFactoryConfig<TModel>, 'label' | 'name'> & {
    readonly label?: string;
    readonly name?: string;
};

export type ReactiveDomainTextFieldFactoryConfig<TModel> = Omit<ReactiveGenericFieldFactoryConfig<TModel>, 'label' | 'name' | 'type'> & {
    readonly label?: string;
    readonly name?: string;
    readonly type?: InputType;
};

export type ReactiveDomainTextareaFieldFactoryConfig<TModel> = Omit<ReactiveTextareaFieldFactoryConfig<TModel>, 'label' | 'name'> & {
    readonly label?: string;
    readonly name?: string;
};

export interface ReactiveDateFieldFactoryConfig<TModel> extends Omit<ReactiveGenericFieldFactoryConfig<TModel>, 'label' | 'name' | 'type'> {
    readonly calendarConfiguration?: CalendarConfiguration;
    readonly label?: string;
    readonly name?: string;
}

/** Builds the standard client selector used by most business forms. */
export class ReactiveClienteFieldFactory {
    public static create<TModel>(config: ReactiveDomainDropdownFieldFactoryConfig<TModel>): FormField<TModel> {
        return ReactiveDropdownFieldFactory.create<TModel>({
            ...config,
            name: config.name ?? DEFAULT_CLIENT_FIELD_NAME,
            label: config.label ?? DEFAULT_CLIENT_LABEL,
            type: config.type ?? ReactiveInputType.DROPDOWN_SEARCH,
            dropdownConfiguration: {
                valuePrimaryKey: DEFAULT_DROPDOWN_PRIMARY_KEY,
                ...(config.dropdownConfiguration ?? {}),
            },
        });
    }
}

/** Builds the standard date field with the application date-picker defaults. */
export class ReactiveFechaFieldFactory {
    public static create<TModel>(config: ReactiveDateFieldFactoryConfig<TModel>): FormField<TModel> {
        return ReactiveFieldFactory.create<TModel>({
            ...config,
            name: config.name ?? DEFAULT_DATE_FIELD_NAME,
            label: config.label ?? DEFAULT_DATE_LABEL,
            type: ReactiveInputType.CALENDAR,
            input: {
                ...(config.input ?? {}),
                calendarConfiguration: {
                    calendarView: 'date',
                    calendarDateFormat: DEFAULT_DATE_FORMAT,
                    ...(config.calendarConfiguration ?? {}),
                },
            },
        });
    }
}

/** Builds the standard observations textarea used across CRUD and task forms. */
export class ReactiveObservacionesFieldFactory {
    public static create<TModel>(config: ReactiveDomainTextareaFieldFactoryConfig<TModel>): FormField<TModel> {
        return ReactiveTextareaFieldFactory.create<TModel>({
            ...config,
            name: config.name ?? DEFAULT_OBSERVATIONS_FIELD_NAME,
            label: config.label ?? DEFAULT_OBSERVATIONS_LABEL,
        });
    }
}

/** Builds the standard product selector while allowing legacy names and multi-select variants. */
export class ReactiveProductoFieldFactory {
    public static create<TModel>(config: ReactiveDomainDropdownFieldFactoryConfig<TModel>): FormField<TModel> {
        return ReactiveDropdownFieldFactory.create<TModel>({
            ...config,
            name: config.name ?? DEFAULT_PRODUCT_FIELD_NAME,
            label: config.label ?? DEFAULT_PRODUCT_LABEL,
            type: config.type ?? ReactiveInputType.DROPDOWN_SEARCH,
            dropdownConfiguration: {
                valuePrimaryKey: DEFAULT_DROPDOWN_PRIMARY_KEY,
                ...(config.dropdownConfiguration ?? {}),
            },
        });
    }
}

/** Builds the standard quantity field. The type remains configurable for legacy text quantities. */
export class ReactiveCantidadFieldFactory {
    public static create<TModel>(config: ReactiveDomainTextFieldFactoryConfig<TModel>): FormField<TModel> {
        return ReactiveFieldFactory.create<TModel>({
            ...config,
            name: config.name ?? DEFAULT_QUANTITY_FIELD_NAME,
            label: config.label ?? DEFAULT_QUANTITY_LABEL,
            type: config.type ?? ReactiveInputType.NUMERIC,
        });
    }
}

/** Builds the standard unit field, usually as text but overridable by each form. */
export class ReactiveUnidadFieldFactory {
    public static create<TModel>(config: ReactiveDomainTextFieldFactoryConfig<TModel>): FormField<TModel> {
        return ReactiveFieldFactory.create<TModel>({
            ...config,
            name: config.name ?? DEFAULT_UNIT_FIELD_NAME,
            label: config.label ?? DEFAULT_UNIT_LABEL,
            type: config.type ?? ReactiveInputType.TEXT,
        });
    }
}

/** Convenience facade for callers that prefer a single import for domain factories. */
export class ReactiveDomainFieldFactory {
    public static cliente<TModel>(config: ReactiveDomainDropdownFieldFactoryConfig<TModel>): FormField<TModel> {
        return ReactiveClienteFieldFactory.create(config);
    }

    public static fecha<TModel>(config: ReactiveDateFieldFactoryConfig<TModel>): FormField<TModel> {
        return ReactiveFechaFieldFactory.create(config);
    }

    public static observaciones<TModel>(config: ReactiveDomainTextareaFieldFactoryConfig<TModel>): FormField<TModel> {
        return ReactiveObservacionesFieldFactory.create(config);
    }

    public static producto<TModel>(config: ReactiveDomainDropdownFieldFactoryConfig<TModel>): FormField<TModel> {
        return ReactiveProductoFieldFactory.create(config);
    }

    public static cantidad<TModel>(config: ReactiveDomainTextFieldFactoryConfig<TModel>): FormField<TModel> {
        return ReactiveCantidadFieldFactory.create(config);
    }

    public static unidad<TModel>(config: ReactiveDomainTextFieldFactoryConfig<TModel>): FormField<TModel> {
        return ReactiveUnidadFieldFactory.create(config);
    }
}
